import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DialogFormFieldsSkeleton,
} from '@shared/ui';
import type { CurrencyDto, ProjectDto } from '@shared/api/contracts';
import {
  useCreateInvoice,
  type CreateInvoiceFormValues,
} from '@features/invoices';
import {
  validateInvoiceField,
  validateInvoiceForm,
  type InvoiceFormField,
} from '@features/invoices';
import {
  trackInvoiceCreateFailed,
  trackInvoiceCreateStarted,
  trackInvoiceCreateSuccess,
} from '@shared/lib/analytics';

interface InvoiceCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: ProjectDto[];
  currencies: CurrencyDto[];
  isMetaLoading?: boolean;
  onSuccess?: () => void;
}

const EMPTY_FORM: CreateInvoiceFormValues = {
  projectId: '',
  amount: '',
  currencyCode: '',
  expires: '',
};

export function InvoiceCreateModal({
  open,
  onOpenChange,
  projects,
  currencies,
  isMetaLoading = false,
  onSuccess,
}: InvoiceCreateModalProps) {
  const { t } = useTranslation('cabinet');
  const { createInvoice, isLoading, error: apiError, clearError } = useCreateInvoice();
  const [form, setForm] = useState<CreateInvoiceFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const timeOptions = useMemo(
    () => [
      { name: t('payments.form.hours24'), value: 86400000 },
      { name: t('payments.form.hours6'), value: 21600000 },
      { name: t('payments.form.hours3'), value: 10800000 },
    ],
    [t]
  );

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setErrors({});
    setSubmitted(false);
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  const revalidateField = (field: InvoiceFormField, nextValues: CreateInvoiceFormValues) => {
    if (!submitted) return;

    const fieldError = validateInvoiceField(field, nextValues, t);
    setErrors((prev) => {
      const next = { ...prev };
      if (fieldError) {
        next[field] = fieldError;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const updateField = <K extends InvoiceFormField>(field: K, value: CreateInvoiceFormValues[K]) => {
    const next = { ...form, [field]: value };
    setForm(next);
    revalidateField(field, next);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);

    const validationErrors = validateInvoiceForm(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    trackInvoiceCreateStarted();
    const result = await createInvoice(form);
    if (result.success) {
      trackInvoiceCreateSuccess('created');
      onOpenChange(false);
      onSuccess?.();
    } else {
      trackInvoiceCreateFailed('create_failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('payments.form.pageTitle')}</DialogTitle>
          <DialogDescription>{t('payments.form.description')}</DialogDescription>
        </DialogHeader>

        {isMetaLoading && !projects.length && !currencies.length ? (
          <DialogFormFieldsSkeleton fields={4} />
        ) : (
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
          {apiError && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
              {apiError}
            </div>
          )}

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="projectId">{t('payments.form.projectLabel')}</Label>
            <Select
              value={form.projectId || undefined}
              onValueChange={(value) => updateField('projectId', value)}
              disabled={isMetaLoading}
            >
              <SelectTrigger id="projectId">
                <SelectValue placeholder={t('payments.form.projectPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.projectId && <p className="text-xs text-destructive sm:text-sm">{errors.projectId}</p>}
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="amount">{t('payments.form.amountLabel')}</Label>
            <Input
              id="amount"
              inputMode="decimal"
              value={form.amount}
              onChange={(event) => updateField('amount', event.target.value)}
              error={errors.amount}
            />
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="currencyCode">{t('payments.form.currencyLabel')}</Label>
            <Select
              value={form.currencyCode || undefined}
              onValueChange={(value) => updateField('currencyCode', value)}
              disabled={isMetaLoading}
            >
              <SelectTrigger id="currencyCode">
                <SelectValue placeholder={t('payments.form.currencyPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((currency) => (
                  <SelectItem key={currency.id} value={currency.code}>
                    {currency.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.currencyCode && (
              <p className="text-xs text-destructive sm:text-sm">{errors.currencyCode}</p>
            )}
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="expires">{t('payments.form.timeLabel')}</Label>
            <Select
              value={form.expires ? String(form.expires) : undefined}
              onValueChange={(value) => updateField('expires', Number(value))}
            >
              <SelectTrigger id="expires">
                <SelectValue placeholder={t('payments.form.timePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {timeOptions.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.expires && <p className="text-xs text-destructive sm:text-sm">{errors.expires}</p>}
          </div>

          <DialogFooter className="px-0 pt-0">
            <Button type="submit" variant="default" loading={isLoading} disabled={isMetaLoading}>
              {t('payments.form.add')}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
